import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router";
import { MultiTicketType } from "@b2bportal/air-shopping-api";
import { useExperiment } from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  FlightShopCardType,
  HalfSheetHashType,
  type IFlightCardProps,
} from "@hopper-b2b/types";
import {
  FlightCardFields,
  Airline,
  AlgomerchTags,
  FlightDuration,
  StopSummary,
  B2BFlightCardV2,
} from "@hopper-b2b/ui";
import {
  getPlusDaysOnSliceWithDates,
  removeTimezone,
} from "@hopper-b2b/utilities";
import { DateTimeFormatStyle, formatDateTime } from "@hopper-i18n/formatter";
import clsx from "clsx";
import { EXPERIMENTS } from "../../../App";
import Airplanes from "../../../assets/client/airplanes.svg?react";
import styles from "./FlightCard.module.scss";

export const FlightCard = (props: IFlightCardProps) => {
  const {
    duration,
    originCode,
    destinationCode,
    departureTime,
    arrivalTime,
    airports,
    primaryCarrier,
    airlineName,
    additionalInfo,
    tags,
    tagsLimit,
    onClickTag,
    currentPriceText,
    rewardText,
    brandName,
    fareMultiTicketType,
  } = props;

  const { language: locale, t } = useI18nContext();
  const plusDays = getPlusDaysOnSliceWithDates(departureTime, arrivalTime);
  const history = useHistory();
  const location = useLocation();
  const departureTimeFormatted = useMemo(
    () =>
      formatDateTime(
        new Date(removeTimezone(departureTime)),
        locale,
        DateTimeFormatStyle.ShortTime
      ),
    [departureTime, locale]
  );

  const arrivalTimeFormatted = useMemo(
    () =>
      formatDateTime(
        new Date(removeTimezone(arrivalTime)),
        locale,
        DateTimeFormatStyle.ShortTime
      ),
    [arrivalTime, locale]
  );

  const enableFlightCardUiUpdate = useExperiment(
    EXPERIMENTS.FLIGHT_CARD_UI_UPDATE
  );

  const hasLayover = airports.length > 0;

  const openFlightDetails = useCallback(
    (e: UIEvent) => {
      e.stopPropagation();
      history.push(
        `${location.pathname}${location.search}#${HalfSheetHashType.FLIGHT_DETAILS}`
      );
    },
    [history, location.pathname, location.search]
  );

  const isVirtualInterlining =
    fareMultiTicketType === MultiTicketType.virtualInterline;

  const airlineString = useMemo(
    () =>
      additionalInfo.numberOfUniqueAirlines > 1
        ? `${airlineName} + ${additionalInfo.numberOfUniqueAirlines - 1}`
        : airlineName,
    [airlineName, additionalInfo]
  );

  // Depending on results of experiment, we can either:
  // (1) If old design > : Obsolete this slot
  // (2) If new design > : Keep slot and remove component rendering after :
  return enableFlightCardUiUpdate ? (
    <FlightCardFields
      className={clsx(
        styles.FlightCard,
        "FlightCard",
        additionalInfo.isExpanded && "is-expanded"
      )}
    >
      {tags?.length > 0 || isVirtualInterlining ? (
        <div className="flight-card-top">
          <AlgomerchTags
            tags={tags}
            tagsLimit={tagsLimit}
            onClickTag={onClickTag}
          />
          {isVirtualInterlining ? (
            <div className="vi-badge">{t("freeMissedConnection")}</div>
          ) : null}
        </div>
      ) : null}
      <div className="flight-card-center">
        <div className="flight-card-time">
          <div className="flight-card-time-departure">
            <div className="flight-card-time-departure-time time">
              {departureTimeFormatted}
            </div>
            <div className="flight-card-time-departure-destination destination">
              {originCode}
            </div>
          </div>
          <div className="flight-card-time-divider">
            {hasLayover ? (
              <div className="flight-card-time-divider-dot"></div>
            ) : null}
          </div>
          <div className="flight-card-time-arrival">
            <div className="flight-card-time-arrival-time time">
              {arrivalTimeFormatted}
              {plusDays > 0 ? (
                <span className="plus-days">+{plusDays}</span>
              ) : null}
            </div>
            <div className="flight-card-time-arrival-destination destination">
              {destinationCode}
            </div>
          </div>
        </div>
        <div className="flight-card-price">{currentPriceText}</div>
      </div>
      <div className="flight-card-bottom">
        {isVirtualInterlining ? (
          <Airplanes className="vi-airline-icon" />
        ) : (
          <Airline
            code={primaryCarrier}
            name={airlineName}
            iconOnly
            size="medium"
          />
        )}
        <div className="flight-card-flight-details">
          <div className="flight-card-duration">
            <FlightDuration duration={duration} />
            <StopSummary airports={airports} hideAirports={true} />
          </div>
          <div className="flight-card-airline">{airlineString}</div>
        </div>
      </div>
      <button onClick={openFlightDetails} className={"see-more-info"}>
        {t("seeMoreInfo")}
      </button>
    </FlightCardFields>
  ) : (
    <B2BFlightCardV2
      duration={duration}
      currentPriceText={currentPriceText}
      rewardText={rewardText}
      originCode={originCode}
      destinationCode={destinationCode}
      departureTime={departureTime}
      arrivalTime={arrivalTime}
      airports={airports}
      brandName={brandName}
      primaryCarrier={primaryCarrier}
      airlineName={airlineName}
      tags={tags}
      cardType={FlightShopCardType.MINI}
      additionalInfo={additionalInfo}
    />
  );
};
